import { useEffect, useState } from 'react';

/**
 * Returns `true` while the window is being resized, staying true until `settleMs` after the
 * last resize event.
 *
 * Use it to suppress CSS transitions that would otherwise animate from width-driven layout
 * changes at responsive breakpoints. For example, a side panel hidden with `translate-x-full`
 * (a width-relative transform) will visibly slide across the viewport when its width switches
 * between a fixed value and full-screen, because `transform` is transitioned and its computed
 * pixel offset changes. Disabling the transition while resizing makes that change instant.
 */
export const useIsResizing = (settleMs: number = 150): boolean => {
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      setIsResizing(true);
      clearTimeout(timer);
      timer = setTimeout(() => setIsResizing(false), settleMs);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [settleMs]);

  return isResizing;
};
